/**
 * The portal answers every failure with one body shape (spec 29 § D-031): RFC 9457 problem+json
 * plus a `code` member and a `traceId`. This module is the only place the SPA takes it apart.
 *
 * Before card 14 twelve controllers each carried a private `formatError`, and the three of them
 * that had drifted rendered validation failures differently from one another. One reader means one
 * rendering, and one place to change when the contract moves.
 */
export interface ProblemDetails {
	type?: string;
	title?: string;
	status?: number;
	detail?: string;
	code?: string;
	traceId?: string;
	errors?: Record<string, string[]>;
}

/**
 * Parses an error body. A body that is not JSON at all is not an error here: it is reported as the
 * detail, so a proxy or gateway page still reaches the user instead of being swallowed.
 */
export function parseProblem(bodyText: string): ProblemDetails {
	try {
		return JSON.parse(bodyText) as ProblemDetails;
	} catch {
		return { detail: bodyText };
	}
}

/**
 * The machine-readable code, and the only field worth branching on. Falls back to the last segment
 * of `type`, which is where the code travelled before it had a member of its own — a stale cached
 * bundle talking to a new server, or the reverse, still matches.
 */
export function problemCode(bodyText: string): string | undefined {
	const problem = parseProblem(bodyText);
	return problem.code ?? problem.type?.split("/").pop();
}

/**
 * The bundle used to translate an error `code` into a sentence. Registered once, by the component,
 * because every caller of `problemMessage` would otherwise have to thread a bundle through - and
 * there are ninety-one of them. Unset until the component initialises, and unset in unit tests,
 * which is why every lookup below tolerates its absence.
 */
let errorBundle: { getText(key: string): string } | undefined;

export function setProblemBundle(bundle: { getText(key: string): string }): void {
	errorBundle = bundle;
}

/**
 * The translated sentence for an error code, or `undefined` when the bundle has nothing for it.
 *
 * A missing key is the normal case, not a failure: the API publishes ninety codes and only the ones
 * a user can actually provoke deserve a sentence. UI5 returns the key itself when it has no entry,
 * so that is how absence is detected - comparing against the key, not against empty.
 */
function codeMessage(code: string | undefined): string | undefined {
	if (!code || !errorBundle) {
		return undefined;
	}
	const key = `error.${code}`;
	const text = errorBundle.getText(key);
	return text && text !== key ? text : undefined;
}

/**
 * A sentence for a human. Validation failures carry their per-field messages in `errors`; a domain
 * rule that the user can hit carries nothing but its `code`, which this resolves against i18n;
 * anything else has `detail` when the server had something to say. The caller supplies the fallback
 * because only it knows what the user was trying to do - and because the fallback comes from i18n,
 * it is the right answer whenever the body has no sentence of its own.
 *
 * The code is consulted BEFORE `detail` on purpose. `detail` is written by the server in one fixed
 * language; the code resolves in the language this user chose. Where both exist, the translated one
 * wins.
 *
 * `title` is deliberately not in the chain: it is a fixed English label the API picks per status
 * ("Not found", "Domain rule violation"), never a sentence for this user in this language. Nor is
 * the raw body — a JSON object the SPA failed to understand is not a message, and rendering it
 * verbatim was how `{"error":"invalid_token"}` reached a MessageBox. A body that is not JSON at all
 * still arrives as `detail`, from `parseProblem`.
 */
export function problemMessage(bodyText: string, fallback: string): string {
	const problem = parseProblem(bodyText);
	if (problem.errors) {
		const messages = Object.values(problem.errors)
			.flat()
			.filter((m) => m && m.length > 0);
		if (messages.length > 0) {
			return messages.join("; ");
		}
	}
	const translated = codeMessage(problem.code ?? problem.type?.split("/").pop());
	if (translated) {
		return translated;
	}
	return problem.detail ?? fallback;
}
